import * as React from 'react';
import { ReactNode, useEffect } from 'react';
import { SearchFacet } from '../../../api/core';
import { IntlShape, useIntl } from 'react-intl';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Checkbox } from 'primereact/checkbox';
import { ExternalSearchParams, QueryFacet } from '../SearchResults';
import { TriStateCheckboxChangeEvent } from 'primereact/tristatecheckbox';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { defaultFacetsPerPage } from '../../../Settings/config';
import { FacetSetting } from '../../../util/types';
import { SelectButton } from 'primereact/selectbutton';
import { getHelp } from '../../../util/intl.utils';
import { HelpToolTip } from '../../components/HelpToolTip';
import { facetSortDefault } from '../utils';
import { userConfig } from '../../../Settings/user-config';

export interface ParsedFacet { key: string; amount: number; factor: boolean | null }

export interface SearchFacetsProps {
  facets: SearchFacet[];
  onChange: (queryFacet: QueryFacet) => void;
  queryFacets: QueryFacet[];
  onFacetPageChange: (e: PaginatorPageChangeEvent, facet: string) => void;
  foffset: { [key: string]: number };
  facetsSettings: FacetSetting[];
  externalParams: ExternalSearchParams;
}

interface Tag {
  selected: boolean;
  facetSetting: FacetSetting;
  facetFactored: SearchFacetWithFactor;
  facet: SearchFacet;
};

export interface GroupedFacets {
  [key: string]: QueryFacet[];
}

export interface SearchFacetWithFactor extends Omit<SearchFacet, 'entries'> {
  entries: ParsedFacet[];
}

export const SearchFacets = (props: SearchFacetsProps): ReactNode => {
  const intl: IntlShape = useIntl();
  const availableFacets: SearchFacet[] = props.facets;
  const [tags, setTags] = React.useState<Tag[]>([]);
  const [activeFacetNames, setActiveFacetNames] = React.useState<string[] | null>(null);
  const [groupedFacets, setGroupedFacets] = React.useState<GroupedFacets>({});
  const visibleTags: Tag[] = tags.filter((tag: Tag): boolean =>
    tag.facetFactored.entries.length > 0 || (tag.facet.missing ?? 0) > 0);
  const activeIndices: number[] = visibleTags.reduce<number[]>((acc: number[], tag: Tag, idx: number): number[] => {
    const isActive: boolean = activeFacetNames === null
      ? userConfig.expandAllFacetsDefault || tag.selected
      : activeFacetNames.includes(tag.facetSetting.name);

    if (isActive) acc.push(idx);
    return acc;
  }, []);

  useEffect(() => {

    const newGroupedFacets: GroupedFacets = {};

    for (const facet of props.queryFacets.values()||[]) {
      (newGroupedFacets[facet.facet] ??= []).push(facet);
    }
    setGroupedFacets(newGroupedFacets);
  }, [props.queryFacets]);

  useEffect(() => {
    const preparedTags: Tag[] = props.facetsSettings
      .filter((facetSetting: FacetSetting): boolean => availableFacets.some((facet: SearchFacet): boolean =>
        facet.name === facetSetting.name && (facet.count > 0 || (facet.missing ?? 0) > 0)))
      .map((facetSetting: FacetSetting): Tag => {
        const facet: SearchFacet = availableFacets.find((searchFacet: SearchFacet): boolean => searchFacet.name === facetSetting.name);

        const factoredEntries: ParsedFacet[] = Object
          .entries(facet.entries)
          .map(([key, amount]: [string, number]): ParsedFacet => (
            {
              key,
              amount,
              factor: groupedFacets[facet.name]?.find((fc: QueryFacet): boolean => fc.value === key)?.factor ?? null,
            }
          ))
          .sort(facetSetting.postSort ?? facetSortDefault);

        const facetFactored: SearchFacetWithFactor = {...facet, entries: factoredEntries};

        const selected: boolean = groupedFacets[facet.name]?.length > 0;

        return {selected, facetSetting, facetFactored, facet};
      });
    setTags(preparedTags);
  }, [availableFacets, groupedFacets, props.facetsSettings]);

  if (availableFacets.length === 0) return <div>{intl.formatMessage({id: 'search.no-facets-available'})}</div>;

  return (
    <Accordion
      multiple={true}
      activeIndex={activeIndices}
      onTabChange={(e): void => {
        const indices: number[] = Array.isArray(e.index) ? e.index : [e.index];
        setActiveFacetNames(indices.map((idx: number): string => visibleTags[idx].facetSetting.name));
      }}>
      {
        visibleTags
          .map(({facet, facetSetting, facetFactored, selected}: {
            selected: boolean;
            facetSetting: FacetSetting;
            facetFactored: SearchFacetWithFactor;
            facet: SearchFacet;
          }): ReactNode => {
            const help: string = getHelp(intl, `${facetSetting.label}.help`);
            const missingSelected: boolean = groupedFacets[facetSetting.name]
              ?.some((fc: QueryFacet): boolean => !!fc.missing) ?? false;
            const valueSelected: boolean = groupedFacets[facetSetting.name]
              ?.some((fc: QueryFacet): boolean => !fc.missing) ?? false;
            const showMissing: boolean = facet.missing !== undefined && facet.missing !== null &&
              facet.missing > 0 &&!facetSetting.tristate && (missingSelected || !valueSelected);
            return (
              <AccordionTab
                key={facetSetting.name}
                contentClassName={'facet-tab'}
                header={
                  <div className={'flex justify-content-between align-items-center'}>
                    {intl.formatMessage({id: facetSetting.label})}
                    <div className={'flex flex-row gap-1 align-items-center'}>
                      {help && <HelpToolTip for={facetSetting.label} help={help}/>}
                      {selected && <i className="pi pi-filter-fill"></i>}
                    </div>
                  </div>
                }
                className={'flex flex-column'}>
                {!missingSelected && facetFactored.entries.map((value): ReactNode => {
                  return (
                    <div key={`${facet.name}-${value.key}`} className={'flex justify-content-between'}>
                      {!facetSetting.tristate && (
                        <div className={'field-checkbox flex align-items-left mb-2'}>
                          <Checkbox
                            inputId={`${facet.name}-${value.key}`}
                            checked={value.factor}
                            disabled={props.externalParams?.facets?.some(fc => fc.facet === facetSetting.name && fc.value === value.key)}
                            onChange={(event): void => props.onChange({
                              facet: facetSetting.name,
                              value: value.key,
                              factor: event.target.checked ? true : null,
                            })}
                          ></Checkbox>
                          <label className={'ml-2 cursor-pointer'} htmlFor={`${facet.name}-${value.key}`}>
                            {facetSetting.itemLabel(value.key)}
                          </label>
                        </div>
                      )}
                      {facetSetting.tristate && (
                        <div className={'w-full mb-1 flex justify-content-between align-items-center'}>
                          <div className={'flex flex-shrink flex-wrap'}>
                            <label className={'ml-2 cursor-pointer'} htmlFor={`${facet.name}-${value.key}`}>
                              {facetSetting.itemLabel(value.key)}
                            </label>
                          </div>
                          <div className={'flex w-12rem justify-content-end'}>
                            <SelectButton
                              value={value.factor}
                              onChange={(event: TriStateCheckboxChangeEvent): void => props.onChange({
                                facet: facetSetting.name,
                                value: value.key,
                                factor: (event.target.value as any) !== '' ? event.target.value : null,
                              })}
                              optionLabel={'name'}
                              optionValue={'value'}
                              options={[
                                {name: intl.formatMessage({id: 'label.yes'}), value: true},
                                {name: intl.formatMessage({id: 'label.all'}), value: null},
                                {name: intl.formatMessage({id: 'label.no'}), value: false},
                              ]}
                              pt={{button: {className: 'w-3rem px-0 py-1 h-2rem'}}}
                            />
                          </div>
                        </div>
                      )}
                      {!facetSetting.tristate && <div>
                        <span>{intl.formatNumber(value.amount)}</span>
                      </div>}
                    </div>
                  );
                })}
                {!missingSelected && facet.count > 0 && <Paginator
                  template={'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink'}
                  first={props?.foffset[facet.name] ?? 0}
                  rows={defaultFacetsPerPage}
                  totalRecords={facet.count}
                  onPageChange={(e: PaginatorPageChangeEvent): void => props.onFacetPageChange(e, facet.name)}/>}
                {showMissing && (
                  <div className={'flex justify-content-between'}>
                    <div className={'field-checkbox flex align-items-left mb-2'}>
                      <Checkbox
                        inputId={`${facet.name}-missing`}
                        checked={missingSelected}
                        disabled={props.externalParams?.facets?.some(fc => fc.facet === facetSetting.name && !!fc.missing)}
                        onChange={(event): void => props.onChange({
                          facet: facetSetting.name,
                          value: '',
                          missing: true,
                          factor: event.target.checked ? true : null,
                        })}></Checkbox>
                      <label className={'ml-2 cursor-pointer'} htmlFor={`${facet.name}-missing`}>
                        {intl.formatMessage({id: facetSetting.missingLabel ?? 'search.facet.missing'})}
                      </label>
                    </div>
                    <div>
                      <span>{intl.formatNumber(facet.missing)}</span>
                    </div>
                  </div>
                )}
              </AccordionTab>
            );
          })
      }
    </Accordion>);
};
